import { List, Tag, Typography } from "antd";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import RequestAppAction from "src/store/slices/appActions";
import BulkSendReopen from "src/components/bulk-send-reopen";
import { TextifyModal } from "src/components/modal";
import { bulkSendBadge, formatBatchRef } from "src/utils/format";

const { Text } = Typography;

interface RecentBulkSendsProps {
  didId: number | null
  sendDisabled?: boolean
}

interface BulkSendAttempt {
  id: number
  status: string
  sentCount: number
  failedCount: number
  createdAt: string
}

interface BulkSendRow {
  id: number
  content: string
  totalCount: number
  sentCount: number
  failedCount: number
  status: string
  createdAt: string
  fileLocations?: string[]
  attempts: BulkSendAttempt[]
}

const RecentBulkSends = forwardRef(({ didId, sendDisabled }: RecentBulkSendsProps, ref) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const modalRef: any = useRef(null);

  const [list, setList] = useState<BulkSendRow[]>([]);
  const [selectedBatch, setSelectedBatch] = useState<BulkSendRow | null>(null);

  const fetchList = () => {
    if (!didId) return;
    dispatch(
      RequestAppAction.getRecentBulkSends({
        id: didId,
        cbSuccess: (res: any) => setList(res?.data ?? []),
        cbFailure: () => setList([]),
      })
    );
  };

  useImperativeHandle(ref, () => ({
    openModal: (title?: string) => {
      // Clear any prior selection and load the latest sends when opening
      setSelectedBatch(null);
      fetchList();
      modalRef.current?.openModal(title || t("heading.recentSends"));
    },
    closeModal: () => {
      setSelectedBatch(null);
      modalRef.current?.closeModal();
    },
  }));

  return (
    <TextifyModal ref={modalRef} className="recent-bulk-sends-modal">
      {selectedBatch ? (
        <BulkSendReopen
          batch={selectedBatch}
          sendDisabled={sendDisabled}
          onClose={() => {
            // Refresh the list on the way back so a just-created resend and the resent badge show
            setSelectedBatch(null);
            fetchList();
          }}
        />
      ) : (
        <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
          <List
            dataSource={list}
            renderItem={(row) => {
              // Collapse a chain to one resent badge plus the latest attempt outcome
              const attempts = row.attempts && row.attempts.length
                ? row.attempts
                : [{ id: row.id, status: row.status, sentCount: row.sentCount, failedCount: row.failedCount, createdAt: row.createdAt }];
              const latest = attempts[attempts.length - 1];
              const resentCount = attempts.length - 1;
              const latestBadge = bulkSendBadge({ status: latest.status, sentCount: latest.sentCount, failedCount: latest.failedCount, isResent: false });
              return (
                <List.Item
                  data-testid={`recent-send-row-${row.id}`}
                  onClick={() => setSelectedBatch(row)}
                  style={{ cursor: "pointer" }}
                >
                  <div style={{ width: "100%" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>{formatBatchRef(row.id)}</Text>
                      <Text ellipsis style={{ flex: 1 }}>
                        {row.content}
                      </Text>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {t("label.sent")}: {row.sentCount}
                      </Text>
                      {row.failedCount > 0 && (
                        <Text type="danger" style={{ fontSize: 12 }}>
                          {t("label.failed")}: {row.failedCount}
                        </Text>
                      )}
                      <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                        {resentCount > 0 && (
                          // One badge stands in for all prior attempts with a count when resent more than once
                          <Tag color="gold" style={{ marginInlineEnd: 0 }}>
                            {t("label.resent")}{resentCount > 1 ? ` ×${resentCount}` : ""}
                          </Tag>
                        )}
                        <Tag color={latestBadge.color} style={{ marginInlineEnd: 0 }}>{t(latestBadge.labelKey)}</Tag>
                      </span>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {new Date(row.createdAt).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}{" "}
                        {new Date(row.createdAt).toLocaleTimeString("en-US", {
                          hour: "numeric",
                          minute: "numeric",
                          hour12: true,
                        })}
                      </Text>
                    </div>
                  </div>
                </List.Item>
              );
            }}
          />
        </div>
      )}
    </TextifyModal>
  );
});

export default RecentBulkSends;
